import { Grid } from "@mui/material";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import ByzCard from "src/components/core-components/ByzCard";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";

interface PainCardProps {
  t: (key: string) => string;
  cardData: any;
  selectedMCPs: Record<string, any>;
  handleChange: (key: string, value: any) => void;
  handleMCPChange: (key: string, value: McParameterDetailResponseDto) => void;
  getMCPParameterDetails: (id: number) => any;
  mcpCacheLoaded: boolean;
  typedFormData: Record<string, any>;
}

const PainScaleCard: React.FC<PainCardProps> = ({
  t,
  cardData,
  selectedMCPs,
  handleChange,
  handleMCPChange,
  getMCPParameterDetails,
  mcpCacheLoaded,
  typedFormData,
}) => {
  return (
    <ByzCard sx={{ mt: 2, p: 2 }}>
      {/*FREQUENCY */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.frequency")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={cardData.mcpConfig.FREQUENCY.mcpId}
            multiple={false}
            handleChange={(val: any) => {
              if (val && !Array.isArray(val)) {
                handleMCPChange(
                  "FREQUENCY",
                  val as McParameterDetailResponseDto
                );
              }
            }}
            value={selectedMCPs.FREQUENCY}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(
              cardData.mcpConfig.FREQUENCY.mcpId
            )}
            cacheLoaded={mcpCacheLoaded}
          />
        </Grid>
      </Grid>
      {/*TYPE_AND_QUALITIES */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.type-and-quality")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={cardData.mcpConfig.TYPE_AND_QUALITIES.mcpId}
            multiple={false}
            handleChange={(val: any) => {
              if (val && !Array.isArray(val)) {
                handleMCPChange(
                  "TYPE_AND_QUALITIES",
                  val as McParameterDetailResponseDto
                );
              }
            }}
            value={selectedMCPs.TYPE_AND_QUALITIES}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(
              cardData.mcpConfig.TYPE_AND_QUALITIES.mcpId
            )}
            cacheLoaded={mcpCacheLoaded}
          />
        </Grid>
      </Grid>
      {/* TYPE_QUALITIES_EXPLANATION */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.explanation")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField
            fullWidth
            value={typedFormData?.TYPE_QUALITIES_EXPLANATION || ""}
            onChange={(e) =>
              handleChange("TYPE_QUALITIES_EXPLANATION", e.target.value)
            }
          />
        </Grid>
      </Grid>

      {/*ACCOMPANYING_SYMPTOMS */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.symptoms")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={cardData.mcpConfig.ACCOMPANYING_SYMPTOMS.mcpId}
            multiple={false}
            handleChange={(val: any) => {
              if (val && !Array.isArray(val)) {
                handleMCPChange(
                  "ACCOMPANYING_SYMPTOMS",
                  val as McParameterDetailResponseDto
                );
              }
            }}
            value={selectedMCPs.ACCOMPANYING_SYMPTOMS}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(
              cardData.mcpConfig.ACCOMPANYING_SYMPTOMS.mcpId
            )}
            cacheLoaded={mcpCacheLoaded}
          />
        </Grid>
      </Grid>

      {/*PLACE_OF_PAIN */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.place-of-pain")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={cardData.mcpConfig.PLACE_OF_PAIN.mcpId}
            multiple={false}
            handleChange={(val: any) => {
              if (val && !Array.isArray(val)) {
                handleMCPChange(
                  "PLACE_OF_PAIN",
                  val as McParameterDetailResponseDto
                );
              }
            }}
            value={selectedMCPs.PLACE_OF_PAIN}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(
              cardData.mcpConfig.PLACE_OF_PAIN.mcpId
            )}
            cacheLoaded={mcpCacheLoaded}
          />
        </Grid>
      </Grid>

      {/* PLACE_PAIN_EXPLANATION */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.explanation")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField
            fullWidth
            value={typedFormData?.PLACE_PAIN_EXPLANATION || ""}
            onChange={(e) =>
              handleChange("PLACE_PAIN_EXPLANATION", e.target.value)
            }
          />
        </Grid>
      </Grid>
      {/* SPREAD_OF_PAIN */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.spread-of-pain")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField
            fullWidth
            multiline
            rows={4}
            value={typedFormData?.SPREAD_OF_PAIN || ""}
            onChange={(e) => handleChange("SPREAD_OF_PAIN", e.target.value)}
          />
        </Grid>
      </Grid>
      {/* SIDE_EFFECTS */}
      <Grid container spacing={2} alignItems="center" mt={1}>
        <Grid item xs={4}>
          <ByzMuiTypography>
            {t("pain-assessment-scale.pain-assessment.left.side-effects")}
          </ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField
            fullWidth
            multiline
            rows={4}
            value={typedFormData?.SIDE_EFFECTS || ""}
            onChange={(e) => handleChange("SIDE_EFFECTS", e.target.value)}
          />
        </Grid>
      </Grid>
    </ByzCard>
  );
};

export default PainScaleCard;
